import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface Point {
  x: number;
  y: number;
}

export interface RectBounds {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export type SubmenuDirection = 'left' | 'right';

export interface SubmenuAim {
  submenuEl: HTMLElement;
  parentEl: HTMLElement;
}

const SAFE_TRIANGLE_PADDING = 12;
const CLOSE_GRACE_MS = 220;
const RECT_HIT_PADDING = 6;

function crossProductSign(origin: Point, a: Point, b: Point) {
  return (
    (a.x - origin.x) * (b.y - origin.y) - (b.x - origin.x) * (a.y - origin.y)
  );
}

export function isPointerInSafeTriangle(
  pointer: Point,
  apex: Point,
  rect: RectBounds,
  direction: SubmenuDirection,
): boolean {
  const nearEdgeX = direction === 'right' ? rect.left : rect.right;
  const top: Point = { x: nearEdgeX, y: rect.top - SAFE_TRIANGLE_PADDING };
  const bottom: Point = {
    x: nearEdgeX,
    y: rect.bottom + SAFE_TRIANGLE_PADDING,
  };

  const first = crossProductSign(pointer, apex, top);
  const second = crossProductSign(pointer, top, bottom);
  const third = crossProductSign(pointer, bottom, apex);

  const hasNegative = first < 0 || second < 0 || third < 0;
  const hasPositive = first > 0 || second > 0 || third > 0;

  return !(hasNegative && hasPositive);
}

function isPointerInRect(
  pointer: Point,
  rect: RectBounds,
  padding = RECT_HIT_PADDING,
) {
  return (
    pointer.x >= rect.left - padding &&
    pointer.x <= rect.right + padding &&
    pointer.y >= rect.top - padding &&
    pointer.y <= rect.bottom + padding
  );
}

function getSubmenuDirection(
  parentRect: RectBounds,
  submenuRect: RectBounds,
): SubmenuDirection {
  return submenuRect.left >= parentRect.right - 1 ? 'right' : 'left';
}

function isPointerInBridge(
  pointer: Point,
  parentRect: RectBounds,
  submenuRect: RectBounds,
  direction: SubmenuDirection,
) {
  const left =
    direction === 'right'
      ? parentRect.right - RECT_HIT_PADDING
      : submenuRect.right - RECT_HIT_PADDING;
  const right =
    direction === 'right'
      ? submenuRect.left + RECT_HIT_PADDING
      : parentRect.left + RECT_HIT_PADDING;

  return isPointerInRect(
    pointer,
    {
      left: Math.min(left, right),
      right: Math.max(left, right),
      top: Math.min(parentRect.top, submenuRect.top),
      bottom: Math.max(parentRect.bottom, submenuRect.bottom),
    },
    0,
  );
}

export interface ActiveSubmenuDescriptor {
  id: string;
  anchorEl: HTMLElement | null;
  items: unknown[];
  onClose: () => void;
}

interface SubmenuIntentValue {
  openId: string | null;
  activeSubmenu: ActiveSubmenuDescriptor | null;
  requestSwitchTo: (id: string | null) => void;
  forceOpen: (id: string) => void;
  close: (id: string) => void;
  cancelScheduledClose: () => void;
  setAim: (aim: SubmenuAim | null) => void;
  setActiveSubmenu: (descriptor: ActiveSubmenuDescriptor) => void;
  clearActiveSubmenu: (id: string) => void;
}

const SubmenuIntentContext = createContext<SubmenuIntentValue | null>(null);

export function SubmenuIntentProvider({
  children,
  open = true,
}: {
  children: ReactNode;
  open?: boolean;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [activeSubmenu, setActiveSubmenuState] =
    useState<ActiveSubmenuDescriptor | null>(null);
  const openIdRef = useRef<string | null>(null);
  const pointerRef = useRef<Point>({ x: 0, y: 0 });
  const apexRef = useRef<Point | null>(null);
  const aimRef = useRef<SubmenuAim | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  openIdRef.current = openId;

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeOpenSubmenu = useCallback(() => {
    cancelScheduledClose();
    apexRef.current = null;
    aimRef.current = null;
    setOpenId(null);
    setActiveSubmenuState(null);
  }, [cancelScheduledClose]);

  const clearActiveSubmenu = useCallback((id: string) => {
    setActiveSubmenuState((previous) =>
      previous?.id === id ? null : previous,
    );
  }, []);

  const scheduleClose = useCallback(() => {
    if (closeTimerRef.current) return;
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      closeOpenSubmenu();
    }, CLOSE_GRACE_MS);
  }, [closeOpenSubmenu]);

  const isPointerInSafeZone = useCallback((pointer: Point) => {
    const aim = aimRef.current;
    if (!aim) return false;
    const submenuRect = aim.submenuEl.getBoundingClientRect();
    const parentRect = aim.parentEl.getBoundingClientRect();

    if (
      isPointerInRect(pointer, submenuRect) ||
      isPointerInRect(pointer, parentRect)
    ) {
      return true;
    }

    const direction = getSubmenuDirection(parentRect, submenuRect);
    if (isPointerInBridge(pointer, parentRect, submenuRect, direction)) {
      return true;
    }

    return (
      apexRef.current !== null &&
      isPointerInSafeTriangle(pointer, apexRef.current, submenuRect, direction)
    );
  }, []);

  const requestSwitchTo = useCallback(
    (id: string | null) => {
      if (id === openIdRef.current) {
        cancelScheduledClose();
        return;
      }
      if (aimRef.current && isPointerInSafeZone(pointerRef.current)) {
        return;
      }
      cancelScheduledClose();
      apexRef.current = null;
      aimRef.current = null;
      setOpenId(id);
    },
    [cancelScheduledClose, isPointerInSafeZone],
  );

  const forceOpen = useCallback(
    (id: string) => {
      cancelScheduledClose();
      apexRef.current = null;
      aimRef.current = null;
      setOpenId(id);
    },
    [cancelScheduledClose],
  );

  const close = useCallback(
    (id: string) => {
      if (openIdRef.current === id) {
        closeOpenSubmenu();
      }
    },
    [closeOpenSubmenu],
  );

  const setAim = useCallback((aim: SubmenuAim | null) => {
    aimRef.current = aim;
  }, []);

  useEffect(() => {
    if (!openId) return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const pointer = { x: event.clientX, y: event.clientY };
      pointerRef.current = pointer;
      const aim = aimRef.current;
      if (!aim) return;

      if (isPointerInRect(pointer, aim.parentEl.getBoundingClientRect())) {
        apexRef.current = pointer;
      }

      if (isPointerInSafeZone(pointer)) {
        cancelScheduledClose();
      } else {
        scheduleClose();
      }
    };

    document.addEventListener('pointermove', handlePointerMove);
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
    };
  }, [openId, isPointerInSafeZone, cancelScheduledClose, scheduleClose]);

  useEffect(() => {
    if (!open) {
      cancelScheduledClose();
      apexRef.current = null;
      aimRef.current = null;
      setOpenId(null);
      setActiveSubmenuState(null);
    }
  }, [open, cancelScheduledClose]);

  useEffect(() => cancelScheduledClose, [cancelScheduledClose]);

  const value = useMemo<SubmenuIntentValue>(
    () => ({
      openId,
      activeSubmenu,
      requestSwitchTo,
      forceOpen,
      close,
      cancelScheduledClose,
      setAim,
      setActiveSubmenu: setActiveSubmenuState,
      clearActiveSubmenu,
    }),
    [
      openId,
      activeSubmenu,
      requestSwitchTo,
      forceOpen,
      close,
      cancelScheduledClose,
      setAim,
      clearActiveSubmenu,
    ],
  );

  return (
    <SubmenuIntentContext.Provider value={value}>
      {children}
    </SubmenuIntentContext.Provider>
  );
}

export function useSubmenuIntent() {
  return useContext(SubmenuIntentContext);
}
